/* ==========================================================================
   UTOPIA COMPUTERS — SECTOR LANDING PAGES
   assets/utopia-sector.js

   Deferred, no dependencies. Every sector section includes this file so any
   of them can be removed or reordered without breaking the others; the guard
   below makes sure it only ever runs once however many times it is loaded.

   Does five small things:
     1. Marks <html> with .sx-js, which is what enables hidden start states.
        Without this script every section renders complete.
     2. Reveals [data-sx-reveal] sections once, as they enter the viewport.
     3. Lights the capability spine for whichever band is hovered or focused,
        and runs the mobile show/hide toggles.
     4. Advances the project timeline with scroll: sets --p (0 to 1) on the
        track and marks each stage the line has reached.
     5. A gentle parallax on the hero landscape — wide screens only, off for
        reduced motion, and paused while the hero is off screen.
   ========================================================================== */

(function () {
  if (window.__utopiaSector) return;
  window.__utopiaSector = true;

  var root = document.documentElement;
  root.classList.add('sx-js');

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {

    /* ---- 1. Reveal once ------------------------------------------------- */
    var reveals = document.querySelectorAll('[data-sx-reveal]');
    if (reduce || !('IntersectionObserver' in window)) {
      reveals.forEach(function (el) { el.classList.add('is-in'); });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
      reveals.forEach(function (el) { io.observe(el); });
    }

    /* ---- 2. Capability spine + mobile toggles ---------------------------- */
    document.querySelectorAll('[data-sx-cap]').forEach(function (sec) {
      var nodes = sec.querySelectorAll('[data-layer]');
      var bands = sec.querySelectorAll('[data-layers]');

      function light(list) {
        nodes.forEach(function (n) {
          n.classList.toggle('is-on', list.indexOf(n.getAttribute('data-layer')) > -1);
        });
      }

      bands.forEach(function (band) {
        var list = (band.getAttribute('data-layers') || '').split('|').filter(Boolean);
        band.addEventListener('mouseenter', function () { light(list); });
        band.addEventListener('focusin', function () { light(list); });
        band.addEventListener('mouseleave', function () { light([]); });
        band.addEventListener('focusout', function (e) {
          if (!band.contains(e.relatedTarget)) light([]);
        });

        var btn = band.querySelector('.sx-band__toggle');
        if (btn) {
          var closed = btn.getAttribute('data-closed');
          var open = btn.getAttribute('data-open');
          btn.addEventListener('click', function () {
            var isOpen = band.classList.toggle('is-open');
            btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            btn.querySelector('span').textContent = isOpen ? open : closed;
            if (isOpen) light(list);
          });
        }
      });
    });

    /* ---- 3. Timeline ------------------------------------------------------
       p runs from 0 when the track's top reaches 72% down the screen, to 1
       when its bottom does. Stage i is lit once p passes i/n, which is
       exactly where its node sits on the horizontal track. */
    var tracks = [];
    document.querySelectorAll('[data-sx-timeline]').forEach(function (sec) {
      var track = sec.querySelector('.sx-tl__track');
      var stages = sec.querySelectorAll('.sx-tl__stage');
      if (track && stages.length) tracks.push({ track: track, stages: stages });
    });

    function setTimeline(t, p) {
      t.track.style.setProperty('--p', p.toFixed(3));
      var n = t.stages.length;
      t.stages.forEach(function (st, i) {
        st.classList.toggle('is-on', p >= i / n + 0.001 || p >= 0.999);
      });
    }

    if (tracks.length) {
      if (reduce) {
        tracks.forEach(function (t) { setTimeline(t, 1); });
      } else {
        var tlTick = false;
        var tlPaint = function () {
          tlTick = false;
          var vh = window.innerHeight;
          tracks.forEach(function (t) {
            var r = t.track.getBoundingClientRect();
            var p = (vh * 0.72 - r.top) / Math.max(r.height, 1);
            setTimeline(t, Math.min(1, Math.max(0, p)));
          });
        };
        var tlScroll = function () {
          if (!tlTick) { tlTick = true; window.requestAnimationFrame(tlPaint); }
        };
        window.addEventListener('scroll', tlScroll, { passive: true });
        window.addEventListener('resize', tlScroll, { passive: true });
        tlPaint();
      }
    }

    /* ---- 4. Hero parallax ------------------------------------------------ */
    if (reduce) return;
    var plates = document.querySelectorAll('[data-sx-parallax]');
    if (!plates.length) return;

    var wide = window.matchMedia('(min-width: 1100px)');
    var visible = new Set();
    var ticking = false;

    function paint() {
      ticking = false;
      if (!wide.matches) {
        plates.forEach(function (p) { p.style.transform = ''; });
        return;
      }
      var vh = window.innerHeight;
      visible.forEach(function (img) {
        var r = img.parentNode.getBoundingClientRect();
        /* -1 when the plate's centre is at the bottom of the screen, +1 at
           the top. Six per cent of travel either way, never more. */
        var t = ((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2);
        img.style.transform = 'translate3d(0,' + (t * -5).toFixed(2) + '%,0)';
      });
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(paint); }
    }

    if ('IntersectionObserver' in window) {
      var pio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) visible.add(e.target); else visible.delete(e.target);
        });
        onScroll();
      });
      plates.forEach(function (p) { pio.observe(p); });
    } else {
      plates.forEach(function (p) { visible.add(p); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  });
})();
